import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaTicketAlt, FaArrowLeft } from 'react-icons/fa'

const DiscoveredEventPreview = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const event = location.state?.event

  // If no event data, redirect back to home
  if (!event) {
    navigate('/home')
    return null
  }

  const handleAddToPassport = () => {
    // Navigate to new event form with the event data
    navigate('/newEvent', { state: { selectedEvent: event } })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBA'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (timeString) => {
    if (!timeString) return null
    return timeString
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <FaArrowLeft />
          <span>Back to Discover</span>
        </button>

        {/* Event Header with Image */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden mb-6">
          {/* Event Image */}
          {event.images && event.images.length > 0 ? (
            <div className="relative h-64 sm:h-96 overflow-hidden">
              <img
                src={event.images[0].url || event.images[0]}
                alt={event.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.parentElement.innerHTML = `
                    <div class="h-64 sm:h-96 flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
                      <span class="text-6xl opacity-50">🎵</span>
                    </div>
                  `
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.9)' }}>{event.name}</h1>
              </div>
            </div>
          ) : (
            <div className="h-64 sm:h-96 flex flex-col items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
              <span className="text-6xl opacity-50 mb-4">🎵</span>
              <h1 className="text-3xl sm:text-4xl font-bold text-white text-center">{event.name}</h1>
            </div>
          )}
        </div>

        {/* Event Details Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-8 mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Event Details</h2>

          <div className="space-y-6">
            {/* Date */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <FaCalendarAlt className="text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Date</h3>
                <p className="text-gray-300">{formatDate(event.date)}</p>
              </div>
            </div>

            {/* Time */}
            {event.time && (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaClock className="text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Time</h3>
                  <p className="text-gray-300">{formatTime(event.time)}</p>
                </div>
              </div>
            )}

            {/* Venue */}
            {event.venue && (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaMapMarkerAlt className="text-pink-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Venue</h3>
                  <p className="text-gray-300">{event.venue.name}</p>
                  {event.venue.city && event.venue.state && (
                    <p className="text-gray-400 text-sm mt-1">
                      {[event.venue.city, event.venue.state, event.venue.country]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Price Range */}
            {event.priceRange && (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaTicketAlt className="text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Price Range</h3>
                  <p className="text-gray-300">
                    ${event.priceRange.min} - ${event.priceRange.max}
                  </p>
                </div>
              </div>
            )}

            {/* Artists */}
            {event.artists && event.artists.length > 0 && (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🎤</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-2">Artists</h3>
                  <div className="flex flex-wrap gap-2">
                    {event.artists.map((artist, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-white/10 border border-white/20 rounded-full text-sm text-gray-300"
                      >
                        {artist.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleAddToPassport}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
          >
            📸 Add to My Passport
          </button>

          {event.url && (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-center"
            >
              View on Ticketmaster
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default DiscoveredEventPreview
